import { EFinancingProgram, getEFinancingProgramList } from '@if/core/types'
import HistoryIcon from '@mui/icons-material/History'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import {
  Alert,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Stack,
  Typography
} from '@mui/material'
import { useRemoveLoansByProgram } from '@src/api/admin-commands-api'
import { useNotification } from '@src/providers/NotificationProvider'
import { DevToolsCommandType } from '@src/types/dev-tools'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import CommandHistoryDialog from './CommandHistoryDialog'
import CommandRecentExecutionsTable from './CommandRecentExecutionsTable'

// Sentinel for the "select all" entry. Local to this card — it is not part of the
// shared EFinancingProgram enum.
const ALL_PROGRAMS = 'all' as const
const PROGRAM_SELECT_LABEL_ID = 'remove-loans-program-select-label'

const CleanLoansByProgramCard = () => {
  const { t } = useTranslation()
  const { showSuccess, showError } = useNotification()
  const removeLoansByProgram = useRemoveLoansByProgram()

  const allPrograms = getEFinancingProgramList()

  const [selectedPrograms, setSelectedPrograms] = useState<EFinancingProgram[]>([])
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [isHistoryDialogOpen, setIsHistoryDialogOpen] = useState(false)

  const isPending = removeLoansByProgram.isPending
  const canSubmit = selectedPrograms.length > 0 && !isPending
  const allSelected = allPrograms.length > 0 && selectedPrograms.length === allPrograms.length

  const programLabel = (program: EFinancingProgram) => t(`EFinancingProgram.${program}`, { ns: 'enum' })

  const confirmationMessage = allSelected
    ? t('admin.devTools.commands.removeAllLoanByProgram.confirmationMessage')
    : t('admin.devTools.commands.removeAllLoanByProgram.confirmationMessageProgram', {
        program: selectedPrograms.map(programLabel).join(', '),
      })

  const handleSelectionChange = (event: SelectChangeEvent<EFinancingProgram[]>) => {
    const value = event.target.value
    const values = typeof value === 'string' ? (value.split(',') as EFinancingProgram[]) : value
    // Clicking the "All" entry toggles the whole list rather than adding a sentinel value.
    if ((values as string[]).includes(ALL_PROGRAMS)) {
      setSelectedPrograms(allSelected ? [] : [...allPrograms])
      return
    }
    setSelectedPrograms(values)
  }

  const handleOpenDialog = () => setIsDialogOpen(true)
  const handleCloseDialog = () => {
    if (!isPending) setIsDialogOpen(false)
  }

  const handleConfirm = async () => {
    if (selectedPrograms.length === 0) return
    try {
      await removeLoansByProgram.mutateAsync({ financingProgramIds: selectedPrograms })
      showSuccess(t('admin.devTools.messages.success'))
      setSelectedPrograms([])
    } catch {
      showError(t('admin.devTools.messages.error'))
    } finally {
      setIsDialogOpen(false)
    }
  }

  return (
    <>
      <Card
        elevation={3}
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          border: '1px solid',
        }}
      >
        <CardHeader
          avatar={<WarningAmberIcon color="warning" />}
          title={<Typography variant="h6">{t('admin.devTools.commands.removeAllLoanByProgram.title')}</Typography>}
        />
        <CardContent sx={{ flexGrow: 1 }}>
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              {t('admin.devTools.commands.removeAllLoanByProgram.description')}
            </Typography>
            <FormControl fullWidth disabled={isPending}>
              <InputLabel id={PROGRAM_SELECT_LABEL_ID}>
                {t('admin.devTools.commands.removeAllLoanByProgram.inputLabel')}
              </InputLabel>
              <Select
                labelId={PROGRAM_SELECT_LABEL_ID}
                multiple
                value={selectedPrograms}
                onChange={handleSelectionChange}
                input={<OutlinedInput label={t('admin.devTools.commands.removeAllLoanByProgram.inputLabel')} />}
                renderValue={(selected) =>
                  allSelected
                    ? t('admin.devTools.commands.removeAllLoanByProgram.allOption')
                    : selected.map(programLabel).join(', ')
                }
              >
                <MenuItem value={ALL_PROGRAMS}>
                  <Checkbox
                    checked={allSelected}
                    indeterminate={selectedPrograms.length > 0 && !allSelected}
                  />
                  <ListItemText primary={t('admin.devTools.commands.removeAllLoanByProgram.allOption')} />
                </MenuItem>
                {allPrograms.map((program) => (
                  <MenuItem key={program} value={program}>
                    <Checkbox checked={selectedPrograms.includes(program)} />
                    <ListItemText primary={programLabel(program)} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </CardContent>
        <CardActions sx={{ justifyContent: 'flex-end', px: 2, pb: 2 }}>
          <Button
            variant="contained"
            color="warning"
            onClick={handleOpenDialog}
            disabled={!canSubmit}
            startIcon={isPending ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {t('admin.devTools.commands.removeAllLoanByProgram.actionLabel')}
          </Button>
        </CardActions>

        <Divider />

        <Stack spacing={1} sx={{ px: 2, py: 2 }}>
          <Typography variant="subtitle2" color="text.secondary">
            {t('admin.devTools.history.recentTitle')}
          </Typography>
          <CommandRecentExecutionsTable commandType={DevToolsCommandType.RemoveAllLoanByProgram} />
          <Stack direction="row" justifyContent="flex-end">
            <Button
              size="small"
              variant="text"
              startIcon={<HistoryIcon />}
              onClick={() => setIsHistoryDialogOpen(true)}
            >
              {t('admin.devTools.history.viewFullHistory')}
            </Button>
          </Stack>
        </Stack>
      </Card>

      <Dialog open={isDialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{t('admin.devTools.dialog.title')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Alert severity="warning">{confirmationMessage}</Alert>
            <DialogContentText>{t('admin.devTools.dialog.prompt')}</DialogContentText>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={isPending}>
            {t('admin.devTools.dialog.cancel')}
          </Button>
          <Button
            onClick={() => void handleConfirm()}
            color="error"
            variant="contained"
            disabled={isPending}
            startIcon={isPending ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {t('admin.devTools.dialog.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      <CommandHistoryDialog
        open={isHistoryDialogOpen}
        onClose={() => setIsHistoryDialogOpen(false)}
        commandType={DevToolsCommandType.RemoveAllLoanByProgram}
        commandTitle={t('admin.devTools.commands.removeAllLoanByProgram.title')}
      />
    </>
  )
}

export default CleanLoansByProgramCard
